import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';
import { z } from 'zod';
import { CapturePointWorkbench, isSampleTourJob } from '@paige/capturepoint';
import { useOpenWorkspaceJob } from '../screens/review/open-workspace-job';
import { newJobGate } from '../screens/fleet/new-job-gate';

/**
 * A new job is made where jobs live: CapturePoint's new-job dialog, mounted over the workbench. The
 * job it saves becomes a Paige job through POST /v1/jobs, and Paige opens it.
 *
 * `?tour=1` is Take a 2-minute tour (the jobs home's welcome): CapturePoint's `capturepoint://tour`
 * link as a Paige address. The workbench makes the tour's ready-made job, opens it on its own route
 * (the tour's job, by its workspace job id) and walks it (paige/app-links.ts, paige/sample-tour.ts).
 */
const searchSchema = z.object({
  tour: z.literal(1).optional().catch(undefined),
  /** `name`: the dialog opens on My own documents' name step (the tour's Start my own job). */
  start: z.literal('name').optional().catch(undefined),
});

export const Route = createFileRoute('/t/$tenant/jobs/new')({
  validateSearch: searchSchema,
  // New job is off on the jobs home for a person the API would refuse; its address is too.
  beforeLoad: async ({ context, params }) => {
    const home = newJobGate(await context.auth.ensure(), params.tenant);
    if (home) throw redirect(home);
  },
  component: RouteComponent,
});

function RouteComponent(): ReactNode {
  const { tenant } = Route.useParams();
  const { tour, start } = Route.useSearch();
  const navigate = useNavigate();
  const openJob = useOpenWorkspaceJob(tenant);
  // Only the tour opens a job from here; a new job of their own opens through onCreated. The
  // tour's job opens however the tour began: the welcome's link (?tour=1) or New job's own Take
  // a 2-minute tour, which made the job and then sat on "Getting the documents ready" for good.
  // One function for the page's life: the workbench hands the tour's link to each bridge once, and
  // a new function is a new bridge (the tour then started twice, on two sample jobs).
  const openTourJob = useCallback(
    (workspaceJobId: string) => {
      if (isSampleTourJob(workspaceJobId)) openJob(workspaceJobId);
    },
    [openJob],
  );
  const home = () => void navigate({ to: '/t/$tenant', params: { tenant } });
  return (
    <CapturePointWorkbench
      engineOrigin="/v1"
      apiOrigin="/v1"
      tenant={tenant}
      onLeave={home}
      onOpenJob={openTourJob}
      {...(tour ? { appLink: 'tour' as const } : {})}
      {...(start ? { createStart: start } : {})}
      onCreated={(job) =>
        void navigate({
          to: '/t/$tenant/jobs/$job/review',
          params: { tenant, job },
          search: {},
        })
      }
    />
  );
}
